/**
 * @module b2b-plus-product-card-msrp
 *
 * Fills every `data-b2b-plus-product-msrp-p-card="<product id>"` element on the page
 * with that product's **MSRP** — the variant `core_price` returned by the catalog-price
 * API (the product's true, pre-catalog price), the same value the PDP / cart / checkout
 * show as "MSRP".
 *
 * Why an attribute scanner and not a custom element: the MSRP has to be placeable
 * anywhere inside a theme card (next to the price, under the title, in a badge), so
 * the contract is a plain data attribute on whatever element the card markup already
 * has. Render it `hidden` and this module reveals it once a price lands:
 *
 *     <span data-b2b-plus-product-msrp-p-card="{{ product.id }}" hidden></span>
 *
 * Per-host opt-ins: `data-msrp-label="false"` renders the amount without the "MSRP"
 * label, `data-msrp-trailing-zeros="false"` renders whole amounts without cents,
 * `data-msrp-catalog-price="<cents>"` (plus `data-msrp-catalog-price-max="<cents>"` when
 * the price varies) supplies the selling price/range to guard against (for hosts with no
 * card product JSON nearby, e.g. the PDP header).
 *
 * Behaviour:
 *   • Hard-gated on `config.msrp.enabled` — off ⇒ this module renders nothing at all
 *     and makes no requests.
 *   • One batched catalog-price lookup per grid, through `getProductJSON()`, so it shares
 *     that cache (in-memory + the `localStorage` bucket for this pricing context) with the
 *     PDP, cart and checkout. The request is SLIM — prices only, no images / vendor /
 *     metafields — so a cached entry it wrote is reused by the PDP's MSRP back-fill but
 *     not by the cart, which needs the full payload. A product whose `core_price` differs
 *     across variants renders "min - max"; when every variant shares one MSRP, a single amount.
 *   • Cards already covered by `b2b-plus-product-card-cta.liquid` carry a product JSON
 *     script; when present its `price` + variant prices give the selling RANGE, used to
 *     suppress an MSRP that is not strictly above it at both ends — i.e. an MSRP below
 *     the wholesale price (nonsensical) or equal to it (nothing to show) — mirroring
 *     `displayProductMsrp()`.
 *   • Fails soft: no price, a failed fetch, or a suppressed value leaves the host
 *     empty and `hidden` (`data-b2b-plus-msrp-state="empty"`), never a broken label.
 *
 * Re-running after the grid changes: {@link refreshProductCardMsrp} is idempotent and
 * also wired to a debounced `MutationObserver`, so filter/sort (Dawn replaces
 * `#ProductGridContainer`), infinite scroll, "load more", and quick-view re-renders
 * hydrate automatically. Call it manually after any custom DOM injection:
 *
 *     window.b2bPlus.functions.refreshProductCardMsrp();          // whole document
 *     window.b2bPlus.functions.refreshProductCardMsrp(container); // one subtree
 *
 * Import map specifier: `b2b-plus/product-card-msrp`.
 * Docs: `b2b-docs/15-b2b-plus-storefront-pricing.md`.
 *
 * Requires a resolvable company location (the catalog-price endpoint is buyer-scoped);
 * without one the lookup fails soft and every host stays hidden.
 */
import { formatMoney } from 'b2b-plus/ui';
import { b2bPlusString, getProductMsrpRanges, isMsrpEnabled } from 'b2b-plus/utils';

/** Host attribute carrying the product id. */
const HOST_ATTR = 'data-b2b-plus-product-msrp-p-card';

/** State stamped on a host so re-scans skip it: `pending` | `ready` | `empty`. */
const STATE_ATTR = 'data-b2b-plus-msrp-state';

/** Quiet period after DOM churn before re-scanning. */
const RESCAN_DEBOUNCE_MS = 120;

/** Un-hydrated hosts inside `root` (a host that already has a state is left alone). */
const pendingHosts = (root) => {
  const scope = root instanceof Element || root instanceof DocumentFragment ? root : document;
  return [...scope.querySelectorAll(`[${HOST_ATTR}]:not([${STATE_ATTR}])`)];
};

/**
 * The catalog (selling) price RANGE in cents for a product id, read from the product
 * JSON that `b2b-plus-product-card-cta.liquid` already renders into the card. Returns
 * null when the card has no JSON (e.g. a non-B2B product, or a card rendered outside
 * the collection template) — the MSRP is then shown unguarded.
 *
 * The range (not just the minimum) matters for a product whose variants disagree on
 * price: an MSRP equal to the TOP of the wholesale range is still "MSRP = wholesale"
 * and must be suppressed, even though it sits above the minimum.
 * @param {string} productId
 * @param {Element} host
 * @returns {{ min: number, max: number }|null}
 */
const catalogPriceFor = (productId, host) => {
  // A host outside a collection card (e.g. the PDP header) has no product JSON
  // script to read; it states its selling price inline instead.
  const inlineMin = Number(host?.dataset?.msrpCatalogPrice);
  const inlineMax = Number(host?.dataset?.msrpCatalogPriceMax);
  if (Number.isFinite(inlineMin)) {
    return { min: inlineMin, max: Number.isFinite(inlineMax) ? inlineMax : inlineMin };
  }

  const script = document.querySelector(
    `script[data-b2b-plus-product-json="${CSS.escape(productId)}"]`
  );
  if (!script) return null;
  try {
    const product = JSON.parse(script.textContent);
    const price = Number(product.price);
    if (!Number.isFinite(price)) return null;
    // Card JSON carries the full variant list — take the widest selling range from it
    // so a varying product is guarded on its maximum as well as its minimum.
    const prices = (Array.isArray(product.variants) ? product.variants : [])
      .map((variant) => Number(variant?.price))
      .filter((cents) => Number.isFinite(cents));
    return {
      min: prices.length ? Math.min(...prices, price) : price,
      max: prices.length ? Math.max(...prices, price) : price,
    };
  } catch {
    return null; // malformed JSON is not worth failing the MSRP over
  }
};

/**
 * Drop a whole-amount's zero cents ("$10.00" → "$10", "€10,00" → "€10"), leaving any
 * currency suffix and any non-zero cents alone — the Liquid `money_without_trailing_zeros`
 * behaviour, for hosts that opt in with `data-msrp-trailing-zeros="false"`.
 * @param {string} money
 * @returns {string}
 */
const stripZeroCents = (money) => money.replace(/([.,])00(?=\D*$)/, '');

/**
 * The money text for a price range: a single amount when the product has one price,
 * "min - max" (`strings.msrpRange`) when it varies — mirroring the range the card shows
 * for its own selling price.
 * @param {{ min: number, max: number }} range
 * @param {boolean} stripZeros
 * @returns {string}
 */
const rangeText = ({ min, max }, stripZeros) => {
  const money = (cents) => (stripZeros ? stripZeroCents(formatMoney(cents)) : formatMoney(cents));
  if (!(max > min)) return money(min);
  return b2bPlusString('msrpRange')
    .replace('{minimum}', money(min))
    .replace('{maximum}', money(max));
};

/**
 * Paint one host. `range == null` (or an MSRP that does not beat the catalog selling
 * range at both ends) empties it.
 * Built with DOM nodes rather than markup, so no interpolation needs escaping.
 *
 * Visibility is driven by the `hidden` attribute: render the host with `hidden` in
 * Liquid and it stays invisible until a real price lands, at which point `hidden` is
 * removed. A host that ends up with no usable price is (re-)hidden, so a card whose
 * MSRP never resolves shows nothing rather than an empty label.
 * @param {Element} host
 * @param {{ min: number, max: number }|null} range
 */
const paint = (host, range) => {
  const productId = host.getAttribute(HOST_ATTR);
  const catalogPrice = catalogPriceFor(productId, host);
  // An MSRP at or under the wholesale price is nonsensical. Guard on BOTH ends of the
  // selling range: the MSRP has to beat the minimum (else it is below wholesale) and
  // the maximum (else it merely repeats a variant's own wholesale price — the
  // "MSRP === wholesale" case, which shows nothing).
  const usable = range != null
    && (catalogPrice == null
      || (range.min > catalogPrice.min && range.max > catalogPrice.max));

  host.textContent = '';
  if (!usable) {
    host.setAttribute(STATE_ATTR, 'empty');
    host.hidden = true;
    return;
  }

  // `data-msrp-label="false"` renders the amount only (for tight card layouts).
  if (host.dataset.msrpLabel !== 'false') {
    const label = document.createElement('span');
    label.className = 'b2bp-card-msrp__label';
    label.textContent = b2bPlusString('msrp');
    host.append(label, document.createTextNode(' '));
  }
  const value = document.createElement('span');
  value.className = 'b2bp-card-msrp__value';
  // `data-msrp-trailing-zeros="false"` renders whole amounts without cents.
  value.textContent = rangeText(range, host.dataset.msrpTrailingZeros === 'false');
  host.appendChild(value);
  host.setAttribute(STATE_ATTR, 'ready');
  host.hidden = false; // reveal — the markup ships with `hidden`
};

/**
 * Hydrate every un-hydrated MSRP host in `root`. Cached prices paint synchronously;
 * only genuinely missing ids go to the network, in one batched request.
 *
 * Idempotent and safe to call as often as you like — already-hydrated hosts are
 * skipped, so this is the function to call after filters, pagination, "load more", or
 * any other markup injection. Resolves once painting is done.
 *
 * @param {Element|Document|DocumentFragment} [root=document] - subtree to scan
 * @returns {Promise<number>} how many hosts were painted with a price
 */
export const refreshProductCardMsrp = async (root = document) => {
  if (!isMsrpEnabled()) return 0;
  const hosts = pendingHosts(root).filter((host) => host.getAttribute(HOST_ATTR));
  if (!hosts.length) return 0;

  // Claim them immediately so a concurrent re-scan (observer + explicit call) can't
  // double-fetch the same cards.
  for (const host of hosts) host.setAttribute(STATE_ATTR, 'pending');

  const ids = [...new Set(hosts.map((host) => host.getAttribute(HOST_ATTR)))];
  // One batched catalog-price lookup for the whole grid, requested SLIM (prices only —
  // no images, vendor or metafields). `getProductJSON()` serves repeats from its
  // in-memory / localStorage cache and coalesces concurrent callers, so a re-scan and
  // the PDP's own MSRP back-fill cost nothing extra. A cart/checkout lookup for the
  // same product does refetch: those need the full payload this one skipped.
  const fetched = await getProductMsrpRanges(ids);

  let painted = 0;
  for (const host of hosts) {
    const id = host.getAttribute(HOST_ATTR);
    if (host.getAttribute(STATE_ATTR) === 'pending') paint(host, fetched[id] ?? null);
    if (host.getAttribute(STATE_ATTR) === 'ready') painted += 1;
  }
  return painted;
};

/** Debounced auto-rescan — one pass per burst of DOM changes. */
let _rescanTimer = null;
const scheduleRescan = () => {
  clearTimeout(_rescanTimer);
  _rescanTimer = setTimeout(() => refreshProductCardMsrp(document), RESCAN_DEBOUNCE_MS);
};

/**
 * Watch for new cards. Dawn's facet filtering replaces `#ProductGridContainer`'s
 * `innerHTML` and load-more/infinite-scroll appends to the grid — both are plain
 * childList mutations, so one observer covers every path without patching the theme.
 * Attributes are deliberately NOT observed: our own painting would retrigger it.
 */
const observeGrid = () => {
  if (!window.MutationObserver || !document.body) return;
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.(`[${HOST_ATTR}]`) || node.querySelector?.(`[${HOST_ATTR}]`)) {
          scheduleRescan();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
};

const init = () => {
  if (!isMsrpEnabled()) return;
  refreshProductCardMsrp(document);
  observeGrid();
};

window.b2bPlus = window.b2bPlus || {};
window.b2bPlus.functions = window.b2bPlus.functions || {};
window.b2bPlus.functions.refreshProductCardMsrp = refreshProductCardMsrp;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
